import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Landmark, Wheat, GraduationCap, PartyPopper, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => `UGX ${Number(v || 0).toLocaleString()}`;

export default function WomenOverviewPage() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/api/women/overview`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>;
  const cats = [
    { icon: Landmark, title: 'Loans Program', lines: [['Disbursed', data.categories.loans_program.disbursed], ['Repayments & Interest', data.categories.loans_program.repayments_and_interest]], path: '/loans', testid: 'women-cat-loans' },
    { icon: Wheat, title: 'Maize Buy-back', lines: [['Purchases', data.categories.maize_buyback.purchases], ['Sales', data.categories.maize_buyback.sales]], path: '/maize', testid: 'women-cat-maize' },
    { icon: GraduationCap, title: 'Training & Workshops', lines: [['Spent', data.categories.training_workshops.spent]], testid: 'women-cat-training' },
    { icon: PartyPopper, title: 'Events', lines: [['Spent', data.categories.events.spent]], testid: 'women-cat-events' },
  ];

  return (
    <div className="space-y-6" data-testid="women-overview-page">
      <div className="flex items-center gap-3">
        <Users className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-heading font-bold">Women Program — Overview</h1>
          <p className="text-sm text-muted-foreground">Live financial position of the Women Program, drawn from the central Finance ledger.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[['Total Program Income', data.total_income, 'text-green-700'], ['Total Spent So Far', data.total_spent, 'text-red-600'], ['Money in the Program (Balance)', data.balance, data.balance >= 0 ? 'text-primary' : 'text-red-600']].map(([l, v, c]) => (
          <Card key={l} data-testid={`women-kpi-${l.split(' ')[1].toLowerCase()}`}><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{l}</p>
            <p className={`text-2xl font-heading font-bold tabular-nums ${c}`}>{fmt(v)}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {cats.map((c) => (
          <Card key={c.title} className={c.path ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} onClick={() => c.path && navigate(c.path)} data-testid={c.testid}>
            <CardHeader className="pb-2"><CardTitle className="font-heading text-sm flex items-center gap-2"><c.icon className="h-4 w-4 text-primary" /> {c.title}</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {c.lines.map(([l, v]) => (
                <div key={l} className="flex justify-between text-xs"><span className="text-muted-foreground">{l}</span><span className="font-medium tabular-nums">{fmt(v)}</span></div>
              ))}
              {c.path && <p className="text-[10px] text-muted-foreground pt-1">Open program →</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card data-testid="women-expense-breakdown">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-sm">Spending Breakdown by Category</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full">
            <thead><tr><th className="px-3 py-1.5 text-left text-[10px] font-semibold uppercase bg-muted border-b">Category</th><th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase bg-muted border-b">Total Spent</th></tr></thead>
            <tbody>
              {data.expense_breakdown.map((b) => (
                <tr key={b.subcategory}><td className="px-3 py-1.5 border-b text-xs">{b.label}</td><td className="px-3 py-1.5 border-b text-xs text-right tabular-nums">{fmt(b.total)}</td></tr>
              ))}
              {data.expense_breakdown.length === 0 && <tr><td colSpan={2} className="text-center py-6 text-muted-foreground text-sm">No Women Program expenses recorded yet</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
